import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Container } from 'reactstrap'
import CurrencyFormat from 'react-currency-format'
import { Link, useHistory } from 'react-router-dom'
import Moment from 'react-moment'
import toastr from 'toastr'

import Breadcrumbs from '../../components/Global/Breadcrumbs'
import FlujoChart from '../../components/charts/FlujoChart'
import ApiService from '../../data/services/ApiService'
import { GeneralContext } from '../../context/'
import {
    calcTasaCobro,
    getAlertBorderColor
} from '../../helpers/dashboard'
import { getDashboardFacturasProgramadas } from '../../helpers/facturasProgramadas'

// ── Design tokens ────────────────────────────────────────────────────────────

const CARD = {
    background: 'var(--color-background-primary, #ffffff)',
    border: '0.5px solid var(--color-border-tertiary, #e9ecef)',
    borderRadius: 'var(--border-radius-md, 6px)',
    padding: '12px 14px',
}

const SECTION_LABEL_STYLE = {
    fontSize: 11,
    fontWeight: 500,
    color: 'var(--color-text-tertiary, #888)',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    marginBottom: 8,
}

const GRID_ROW = { display: 'grid', gap: 12, marginBottom: 12 }

const RESPONSIVE_STYLES = `
    .dash-kpi-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }
    .dash-chart-grid {
        grid-template-columns: 2fr 1fr;
    }
    .dash-3col-grid {
        grid-template-columns: 1fr 1fr 1fr;
    }
    @media (max-width: 1024px) {
        .dash-kpi-grid {
            grid-template-columns: repeat(2, 1fr);
        }
        .dash-chart-grid {
            grid-template-columns: 1fr;
        }
        .dash-3col-grid {
            grid-template-columns: repeat(2, 1fr);
        }
    }
    @media (max-width: 640px) {
        .dash-kpi-grid,
        .dash-chart-grid,
        .dash-3col-grid {
            grid-template-columns: 1fr;
        }
    }
`

// ── Bitácora tipo metadata ───────────────────────────────────────────────────
//
// Color-coding is inferred from `tipo` via a static lookup map.
// The enum is server-controlled and closed, so this is the right long-term
// approach: add new entries here whenever a new tipo is added to the model.
// If we ever need server-driven severity (e.g. to surface it in push
// notifications too), we can add a `severity` field ('success'|'warning'|
// 'error'|'info') to the Bitacora model and drop this map.

const TIPO_META = {
    factura_timbrada:               { color: '#34c38f', label: 'Timbrada', icon: 'mdi-check-circle' },
    factura_borrador:               { color: '#74788d', label: 'Borrador', icon: 'mdi-file-document-edit-outline' },
    factura_cancelada:              { color: '#f46a6a', label: 'Cancelada', icon: 'mdi-cancel' },
    factura_programada:             { color: '#50a5f1', label: 'Programada', icon: 'mdi-calendar-clock' },
    factura_programada_actualizada: { color: '#50a5f1', label: 'Actualizada', icon: 'mdi-calendar-refresh' },
    factura_ejecutada:              { color: '#34c38f', label: 'Ejecutada', icon: 'mdi-check-circle' },
    factura_fallo:                  { color: '#f1b44c', label: 'Falló', icon: 'mdi-alert-circle' },
    factura_saldo_cerrado:          { color: 'var(--color-text-secondary, #74788d)', label: 'Saldo cerrado', icon: 'mdi-check-circle' },
    complemento_timbrado:           { color: '#34c38f', label: 'Comp. timbrado', icon: 'mdi-receipt' },
    complemento_cancelado:          { color: '#f46a6a', label: 'Comp. cancelado', icon: 'mdi-close-circle' },
    contador_invitado:              { color: '#50a5f1', label: 'Contador invitado', icon: 'mdi-account-plus' },
    invitacion_reenviada:           { color: '#50a5f1', label: 'Inv. reenviada', icon: 'mdi-email-sync-outline' },
    invitacion_aceptada:            { color: '#34c38f', label: 'Inv. aceptada', icon: 'mdi-account-check' },
    plan_activado:                  { color: '#34c38f', label: 'Plan activado', icon: 'mdi-star' },
    plan_renovado:                  { color: '#34c38f', label: 'Plan renovado', icon: 'mdi-refresh' },
    plan_cancelado:                 { color: '#f46a6a', label: 'Plan cancelado', icon: 'mdi-close-circle' },
    plan_upgrade:                   { color: '#50a5f1', label: 'Cambio de plan', icon: 'mdi-arrow-up-circle' },
    plan_downgrade_programado:      { color: '#f1b44c', label: 'Cambio de plan programado', icon: 'mdi-clock-outline' },
    plan_downgrade_aplicado:        { color: 'var(--color-text-secondary, #74788d)', label: 'Cambio de plan aplicado', icon: 'mdi-arrow-down-circle' },
    credits_purchased:              { color: '#34c38f', label: 'Timbres comprados', icon: 'mdi-plus-circle' },
    credits_burned:                 { color: 'var(--color-text-tertiary, #adb5bd)', label: 'Timbre usado', icon: 'mdi-circle-small', quiet: true },
}

const getTipoMeta = (tipo) => TIPO_META[tipo] || { color: '#74788d', label: tipo, icon: 'mdi-circle' }

const FACTURA_LINK_TYPES = [
    'factura_timbrada',
    'factura_borrador',
    'factura_cancelada',
    'factura_programada',
    'factura_programada_actualizada',
    'factura_ejecutada',
    'factura_saldo_cerrado',
]

const BILLING_LINK_TYPES = [
    'plan_activado',
    'plan_renovado',
    'plan_cancelado',
    'plan_upgrade',
    'plan_downgrade_programado',
    'plan_downgrade_aplicado',
    'credits_purchased',
]

const getRecordId = (value) => {
    if (!value) {
        return ''
    }

    if (typeof value === 'string') {
        return value
    }

    return value._id || ''
}

const truncateText = (value = '', max = 80) => {
    if (!value || value.length <= max) {
        return value
    }

    return `${value.slice(0, max - 1)}…`
}

const formatShortDate = (value) => {
    if (!value) {
        return ''
    }

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
        return ''
    }

    const day = `${date.getDate()}`.padStart(2, '0')
    const month = `${date.getMonth() + 1}`.padStart(2, '0')

    return `${day}/${month}/${date.getFullYear()}`
}

const getEventLink = (ev) => {
    const facturaId = getRecordId(ev?.metadata?.facturaId) || getRecordId(ev?.facturaId)
    const complementoId = getRecordId(ev?.metadata?.complementoId) || getRecordId(ev?.complementoId)

    if (ev.tipo === 'factura_fallo') {
        return {
            to: '/facturas/programadas',
            label: 'Ver factura programada →',
            cta: true,
            color: '#f1b44c',
        }
    }

    if (FACTURA_LINK_TYPES.includes(ev.tipo) && facturaId) {
        const useDetailView = [
            'factura_timbrada',
            'factura_ejecutada',
            'factura_cancelada',
            'factura_saldo_cerrado',
        ].includes(ev.tipo)

        return {
            to: useDetailView ? `/facturas/${facturaId}` : `/facturas/nueva/${facturaId}`,
            label: 'Ver factura →',
        }
    }

    if (['complemento_timbrado', 'complemento_cancelado'].includes(ev.tipo) && complementoId) {
        return {
            to: '/complementos',
            label: 'Ver complementos →',
        }
    }

    if (BILLING_LINK_TYPES.includes(ev.tipo)) {
        return {
            to: '/configuracion/pagos',
            label: 'Ver pagos →',
        }
    }

    return null
}

const getEventSecondaryLine = (ev) => {
    const metadata = ev?.metadata || {}

    if (['factura_timbrada', 'factura_ejecutada'].includes(ev.tipo)) {
        const parts = [metadata.folio && `Folio ${metadata.folio}`, metadata.receptor].filter(Boolean)
        return parts.length > 0 ? { text: parts.join(' · ') } : null
    }

    if (ev.tipo === 'factura_fallo') {
        return metadata.errorReason
            ? { text: truncateText(metadata.errorReason), color: '#f46a6a' }
            : null
    }

    if (['factura_programada', 'factura_programada_actualizada'].includes(ev.tipo)) {
        const scheduledFor = formatShortDate(metadata.scheduledFor)
        return scheduledFor ? { text: `Programada para ${scheduledFor}` } : null
    }

    if (ev.tipo === 'credits_purchased') {
        return metadata.quantity
            ? { text: `+${metadata.quantity} timbres`, color: '#34c38f' }
            : null
    }

    if (['plan_upgrade', 'plan_downgrade_aplicado'].includes(ev.tipo)) {
        return metadata.message ? { text: metadata.message } : null
    }

    return null
}

// ── Shared sub-components ────────────────────────────────────────────────────

const CurrencyValue = ({ value }) => (
    <CurrencyFormat
        value={value || 0}
        displayType="text"
        thousandSeparator
        prefix="$"
        decimalScale={2}
    />
)

const KpiCard = ({ label, value, subLabel }) => (
    <div style={CARD}>
        <div style={{ fontSize: 11, color: 'var(--color-text-secondary, #888)', marginBottom: 4 }}>
            {label}
        </div>
        <div style={{ fontSize: 16, fontWeight: 500, lineHeight: 1.2, marginBottom: 4 }}>
            {value}
        </div>
        {subLabel && (
            <div style={{ fontSize: 11, color: 'var(--color-text-secondary, #888)' }}>
                {subLabel}
            </div>
        )}
    </div>
)

const CardHeader = ({ title, linkTo, linkLabel }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <strong style={{ fontSize: 13, fontWeight: 600 }}>{title}</strong>
        {linkTo && (
            <Link to={linkTo} style={{ fontSize: 11, color: '#378ADD', textDecoration: 'none' }}>
                {linkLabel}
            </Link>
        )}
    </div>
)

const SectionLabel = ({ children }) => (
    <div style={SECTION_LABEL_STYLE}>{children}</div>
)

// ── Dashboard ────────────────────────────────────────────────────────────────

const Dashboard = () => {
    const { setLoading } = useContext(GeneralContext)
    const history = useHistory()

    const [resumen, setResumen] = useState(null)
    const [alertas, setAlertas] = useState([])
    const [flujo, setFlujo] = useState(null)
    const [cobranza, setCobranza] = useState([])
    const [clientesTop, setClientesTop] = useState([])
    const [bitacoraEventos, setBitacoraEventos] = useState([])
    const [programadas, setProgramadas] = useState([])
    const [egresosResumen, setEgresosResumen] = useState(null)

    useEffect(() => {
        ;(async () => {
            try {
                setLoading(true)

                const now = new Date()
                const yearStart = new Date(now.getFullYear(), 0, 1)
                const yearEnd = new Date(now.getFullYear() + 1, 0, 1)

                const [
                    resumenResponse,
                    alertasResponse,
                    graficaResponse,
                    cobranzaResponse,
                    clientesTopResponse,
                    bitacoraResponse,
                    programadasResponse,
                    egresosResponse,
                ] = await Promise.all([
                    ApiService.getDashboardResumen(),
                    ApiService.getDashboardAlertas(),
                    ApiService.obtenerGraficaAnual({ fromDate: yearStart, toDate: yearEnd }),
                    ApiService.getDashboardCobranza(),
                    ApiService.getDashboardClientesTop(),
                    ApiService.getBitacora({ limit: 8 }),
                    ApiService.getFacturasProgramadas(),
                    ApiService.getResumenEgresos(),
                ])

                setResumen(resumenResponse.data?.data?.mesActual || null)
                setAlertas(alertasResponse.data?.data?.alertas || [])
                setFlujo({
                    categories: graficaResponse.data?.categories || [],
                    series: graficaResponse.data?.series || { facturado: [], cobrado: [] }
                })
                setCobranza(cobranzaResponse.data?.data?.facturasPendientes || [])
                setClientesTop(clientesTopResponse.data?.data?.clientes || [])
                setBitacoraEventos(bitacoraResponse.data?.data?.eventos || [])

                const facturas = programadasResponse.data?.data?.facturas || []
                const { programadas: prox } = getDashboardFacturasProgramadas(facturas)
                setProgramadas(prox.slice(0, 5))
                setEgresosResumen(egresosResponse.data?.data || null)
            } catch (error) {
                toastr.error(error.response?.data?.message || 'No pude cargar el dashboard.')
            } finally {
                setLoading(false)
            }
        })()

        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        ;(async () => {
            const search = new URLSearchParams(history.location.search || '')
            const duplicateId = search.get('duplicate')
            const mode = search.get('mode')

            if (!duplicateId || mode !== 'pue-expired-ppd') {
                return
            }

            try {
                setLoading(true)
                const response = await ApiService.duplicarFactura(duplicateId, { mode })
                if (!response.data?.success) {
                    toastr.error(response.data?.message || 'No pude preparar la factura sustituta.')
                    return
                }

                history.replace('/facturas/nueva', {
                    duplicatedFactura: response.data?.data?.factura
                })
            } catch (error) {
                toastr.error(error.response?.data?.message || 'No pude preparar la factura sustituta.')
            } finally {
                setLoading(false)
            }
        })()

        // eslint-disable-next-line
    }, [history.location.search])

    const tasaCobro = useMemo(
        () => calcTasaCobro(resumen?.facturado, resumen?.cobrado),
        [resumen]
    )

    return (
        <React.Fragment>
            <style>{RESPONSIVE_STYLES}</style>
            <Container fluid>
                <Breadcrumbs
                    title="Facturación"
                    breadcrumbItem="Dashboard"
                    parentLink="/dashboard"
                />

                {/* ── Row 1: KPI strip ──────────────────────────────────── */}
                {resumen && (
                    <>
                        <SectionLabel>RESUMEN DEL MES</SectionLabel>
                        {/* Financial KPIs */}
                        <div className="dash-kpi-grid" style={GRID_ROW}>
                            <KpiCard
                                label="Facturado este mes"
                                value={<CurrencyValue value={resumen.facturado} />}
                                subLabel="Ingresos emitidos"
                            />
                            <KpiCard
                                label="Cobrado este mes"
                                value={<CurrencyValue value={resumen.cobrado} />}
                                subLabel="Monto ya recuperado"
                            />
                            <KpiCard
                                label="Por cobrar"
                                value={<CurrencyValue value={resumen.porCobrar} />}
                                subLabel="Saldo pendiente total"
                            />
                            <KpiCard
                                label="Tasa de cobro"
                                value={tasaCobro}
                                subLabel="cobrado / facturado"
                            />
                        </div>
                        {/* Ops KPIs */}
                        <div className="dash-kpi-grid" style={GRID_ROW}>
                            <KpiCard
                                label="Facturas emitidas este mes"
                                value={resumen.facturasEmitidas || 0}
                                subLabel="CFDI timbrados este mes"
                            />
                            <KpiCard
                                label="Ticket promedio"
                                value={<CurrencyValue value={resumen.ticketPromedio} />}
                                subLabel="Promedio por factura"
                            />
                            <KpiCard
                                label="Timbres disponibles"
                                value={resumen.timbresDisponibles || 0}
                                subLabel={resumen.plan?.nombre ? `${resumen.plan.nombre} activo` : 'Plan activo'}
                            />
                            <KpiCard
                                label="Canceladas este mes"
                                value={resumen.canceladasEsteMes ?? 0}
                                subLabel="facturas canceladas"
                            />
                        </div>
                    </>
                )}

                {/* ── Row 2: Alertas (omitted entirely when empty) ─────── */}
                {alertas.length > 0 && (
                    <>
                        <SectionLabel>ALERTAS</SectionLabel>
                        <div style={{ ...GRID_ROW, gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                            {alertas.map((alerta) => (
                                <div
                                    key={alerta.code}
                                    style={{
                                        background: 'var(--color-background-primary, #ffffff)',
                                        borderTop: '0.5px solid var(--color-border-tertiary, #e9ecef)',
                                        borderRight: '0.5px solid var(--color-border-tertiary, #e9ecef)',
                                        borderBottom: '0.5px solid var(--color-border-tertiary, #e9ecef)',
                                        borderLeft: `3px solid ${getAlertBorderColor(alerta.type)}`,
                                        borderRadius: '0 var(--border-radius-md, 6px) var(--border-radius-md, 6px) 0',
                                        padding: '12px 14px',
                                    }}
                                >
                                    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4, color: 'var(--color-text-primary, #222)' }}>
                                        {alerta.title}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--color-text-secondary, #666)', marginBottom: 8 }}>
                                        {alerta.message}
                                    </div>
                                    {alerta.ctaUrl && (
                                        <Link
                                            to={alerta.ctaUrl}
                                            style={{ fontSize: 12, color: '#378ADD', textDecoration: 'none' }}
                                        >
                                            {alerta.ctaLabel || 'Ver más'}
                                        </Link>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {/* ── Row 3: Chart 2fr · Salud operativa 1fr ───────────── */}
                <div className="dash-chart-grid" style={GRID_ROW}>
                    {/* Facturado vs cobrado */}
                    <div style={CARD}>
                        <div style={{ marginBottom: 12 }}>
                            <strong style={{ fontSize: 13 }}>Facturado vs cobrado</strong>
                        </div>
                        {flujo && (
                            <FlujoChart
                                series={flujo.series || {}}
                                categories={flujo.categories || []}
                            />
                        )}
                    </div>

                    {/* Bitácora */}
                    <div style={CARD}>
                        <CardHeader
                            title="Actividad reciente"
                        />
	                        {bitacoraEventos.length === 0 ? (
	                            <p style={{ color: 'var(--color-text-secondary, #666)', fontSize: 12, margin: 0 }}>
	                                No hay actividad registrada aún.
	                            </p>
	                        ) : (
	                            bitacoraEventos.map((ev, i) => {
	                                const meta = getTipoMeta(ev.tipo)
	                                const link = getEventLink(ev)
	                                const secondaryLine = getEventSecondaryLine(ev)
	                                return (
	                                    <div
	                                        key={ev._id || i}
	                                        style={{
	                                            display: 'flex',
	                                            alignItems: 'flex-start',
	                                            padding: '8px 0',
	                                            borderBottom: '0.5px solid var(--color-border-tertiary, #e9ecef)'
	                                        }}
	                                    >
	                                        <div style={{
	                                            width: 16,
	                                            color: meta.color,
	                                            fontSize: meta.quiet ? 13 : 15,
	                                            lineHeight: '15px',
	                                            opacity: meta.quiet ? 0.65 : 1,
	                                            marginTop: 1,
	                                            flexShrink: 0,
	                                            marginRight: 10
	                                        }}>
	                                            <i className={`mdi ${meta.icon}`}></i>
	                                        </div>
	                                        <div style={{ flex: 1, minWidth: 0 }}>
	                                            <div style={{
	                                                fontSize: 12,
	                                                color: 'var(--color-text-primary, #222)',
	                                                marginBottom: 2,
	                                                whiteSpace: 'nowrap',
	                                                overflow: 'hidden',
	                                                textOverflow: 'ellipsis'
	                                            }}>
	                                                {ev.descripcion}
	                                            </div>
	                                            {secondaryLine && (
	                                                <div style={{
	                                                    fontSize: 11,
	                                                    color: secondaryLine.color || 'var(--color-text-tertiary, #888)',
	                                                    marginTop: 2,
	                                                    whiteSpace: 'nowrap',
	                                                    overflow: 'hidden',
	                                                    textOverflow: 'ellipsis'
	                                                }}>
	                                                    {secondaryLine.text}
	                                                </div>
	                                            )}
	                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
	                                                <span style={{ fontSize: 10, color: meta.color, fontWeight: 500 }}>
	                                                    {meta.label}
	                                                </span>
	                                                <span style={{ fontSize: 10, color: 'var(--color-text-tertiary, #888)' }}>
	                                                    {ev.usuarioId?.nombre ? `por ${ev.usuarioId.nombre} · ` : 'Sistema · '}
	                                                    <Moment fromNow>{ev.createdAt}</Moment>
	                                                </span>
	                                            </div>
	                                            {link && (
	                                                <Link
	                                                    to={link.to}
	                                                    style={{
	                                                        display: 'inline-block',
	                                                        marginTop: 4,
	                                                        fontSize: 11,
	                                                        fontWeight: link.cta ? 600 : 500,
	                                                        color: link.color || '#378ADD',
	                                                        textDecoration: 'none',
	                                                    }}
	                                                >
	                                                    {link.label}
	                                                </Link>
	                                            )}
	                                        </div>
	                                    </div>
	                                )
	                            })
	                        )}
                    </div>
                </div>

                {/* ── Row 4: Cobranza · Clientes top · Programadas ─────── */}
                <div className="dash-3col-grid" style={GRID_ROW}>
                    {/* Cobranza accionable */}
                    <div style={CARD}>
                        <CardHeader
                            title="Cobranza accionable"
                            linkTo="/facturas/porcobrar"
                            linkLabel="Ver todas"
                        />
                        {cobranza.length > 0 ? (
                            cobranza.slice(0, 5).map((factura) => (
                                <div
                                    key={factura._id}
                                    style={{ padding: '10px 0', borderBottom: '0.5px solid var(--color-border-tertiary, #e9ecef)' }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div>
                                            <div style={{ fontWeight: 500, fontSize: 13 }}>{factura.cliente}</div>
                                            <div style={{ fontSize: 11, color: 'var(--color-text-secondary, #666)', marginTop: 2 }}>
                                                {factura.folio ? `Folio ${factura.folio}` : 'Sin folio'} · {factura.diasPendiente} día{factura.diasPendiente === 1 ? '' : 's'} pendiente
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
                                            <div style={{ fontWeight: 500, fontSize: 13 }}>
                                                <CurrencyValue value={factura.saldoPendiente} />
                                            </div>
                                            <div style={{ fontSize: 11, color: 'var(--color-text-secondary, #666)' }}>Pendiente</div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p style={{ color: 'var(--color-text-secondary, #666)', fontSize: 12, margin: 0 }}>
                                No hay facturas pendientes por cobrar.
                            </p>
                        )}
                    </div>

                    {/* Clientes top del año */}
                    <div style={CARD}>
                        <CardHeader
                            title="Clientes top del año"
                            linkTo="/clientes"
                            linkLabel="Ver clientes"
                        />
                        {clientesTop.length > 0 ? (
                            clientesTop.slice(0, 5).map((cliente, index) => (
                                <div
                                    key={cliente.clienteId}
                                    style={{ padding: '10px 0', borderBottom: '0.5px solid var(--color-border-tertiary, #e9ecef)' }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div>
                                            <div style={{ fontWeight: 500, fontSize: 13 }}>{index + 1}. {cliente.nombre}</div>
                                            <div style={{ fontSize: 11, color: 'var(--color-text-secondary, #666)', marginTop: 2 }}>
                                                {cliente.facturas} factura{cliente.facturas === 1 ? '' : 's'} este año
                                            </div>
                                        </div>
                                        <div style={{ fontWeight: 500, fontSize: 13, flexShrink: 0, marginLeft: 8 }}>
                                            <CurrencyValue value={cliente.facturado} />
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p style={{ color: 'var(--color-text-secondary, #666)', fontSize: 12, margin: 0 }}>
                                Todavía no hay clientes destacados este año.
                            </p>
                        )}
                    </div>

                    {/* Próximas facturas programadas */}
                    <div style={CARD}>
                        <CardHeader
                            title="Próximas facturas programadas"
                            linkTo="/facturas/programadas"
                            linkLabel="Ver todas"
                        />
                        {programadas.length > 0 ? (
                            programadas.map((factura) => (
                                <div
                                    key={factura._id}
                                    style={{ padding: '10px 0', borderBottom: '0.5px solid var(--color-border-tertiary, #e9ecef)' }}
                                >
                                    <div style={{ fontWeight: 500, fontSize: 13 }}>
                                        {factura.clientes?.[0]?.razonSocial || 'Sin receptor'}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--color-text-secondary, #666)', marginTop: 2 }}>
                                        <Moment format="DD/MM/YYYY HH:mm">{factura.schedule?.scheduledFor}</Moment>
                                        {factura.schedule?.isRecurring && ' · Recurrente'}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p style={{ color: 'var(--color-text-secondary, #666)', fontSize: 12, margin: 0 }}>
                                No hay facturas programadas próximas.
                            </p>
                        )}
                    </div>
                </div>

                {egresosResumen?.totals?.totalEgresos > 0 && (
                    <>
                        <SectionLabel>EGRESOS</SectionLabel>
                        <div style={{ ...GRID_ROW, gridTemplateColumns: '1fr' }}>
                            <div style={CARD}>
                                <CardHeader title="Gastos y deducciones" linkTo="/egresos" linkLabel="Ver egresos" />
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                                    <div>
                                        <div style={{ fontSize: 11, color: 'var(--color-text-secondary, #888)' }}>Gastos este mes</div>
                                        <strong><CurrencyValue value={egresosResumen.mesActual?.totalGastado} /> MXN</strong>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 11, color: 'var(--color-text-secondary, #888)' }}>Deducibles</div>
                                        <strong><CurrencyValue value={egresosResumen.mesActual?.totalDeducible} /></strong>
                                    </div>
                                    {egresosResumen.mesActual?.pendientesComprobante > 0 && (
                                        <Link
                                            to="/egresos?status=pendiente_comprobante"
                                            style={{
                                                borderLeft: '3px solid #f1b44c',
                                                paddingLeft: 10,
                                                color: '#8a5b00',
                                                textDecoration: 'none',
                                                fontSize: 13,
                                                fontWeight: 600
                                            }}
                                        >
                                            {egresosResumen.mesActual.pendientesComprobante} gastos sin comprobante
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* ── Row 5: Nueva factura quick access ────────────────── */}
                <div
                    style={{
                        border: '0.5px dashed var(--color-border-secondary, #dee2e6)',
                        background: 'var(--color-background-secondary, #f8f9fa)',
                        borderRadius: 'var(--border-radius-md, 6px)',
                        padding: 16,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        marginBottom: 12,
                    }}
                    role="button"
                    tabIndex={0}
                    onClick={() => history.push('/facturas/nueva')}
                    onKeyDown={(e) => e.key === 'Enter' && history.push('/facturas/nueva')}
                >
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary, #222)' }}>
                        + Nueva factura
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-secondary, #888)', marginTop: 4 }}>
                        acceso rápido
                    </div>
                </div>
            </Container>
        </React.Fragment>
    )
}

export default Dashboard
